import { useState, useEffect, useCallback } from 'react';
import { StickyNote, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
};

export const NoteCard = ({ note, actions }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`mynote-${note.id}`}>
    <div className="flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase ${STATUS_STYLES[note.status] || 'bg-gray-100 text-gray-600'}`}>{note.status}</span>
          <span className="text-sm font-bold text-gray-800">{note.title}</span>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px]">
          <span className="text-blue-600 font-medium">{(note.carriers || []).join(', ')}</span>
          {note.state && <span className="text-green-600">{note.state}</span>}
          {note.lob && <span className="text-purple-600">{note.lob}</span>}
          {note.effective_date && <span className="text-gray-400">Eff: {note.effective_date}</span>}
          <span className="text-gray-400 ml-auto">{new Date(note.created_at).toLocaleDateString()}</span>
        </div>
        <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{note.content}</p>
        {note.source_note && <p className="text-[10px] text-gray-400 mt-1">Source: {note.source_note}</p>}
        {note.status === 'declined' && note.decline_reason && (
          <div className="mt-2 bg-red-50 border border-red-100 rounded-lg p-2" data-testid={`decline-reason-${note.id}`}>
            <p className="text-[9px] font-bold text-red-500 uppercase">Declined by {note.reviewed_by_name || 'approver'}</p>
            <p className="text-xs text-red-700 mt-0.5">{note.decline_reason}</p>
          </div>
        )}
        {note.status === 'approved' && (
          <p className="text-[10px] text-green-600 mt-2">
            Approved by {note.reviewed_by_name || 'approver'} · {note.index_status === 'ready' ? 'live in AI Chat' : 'will appear in AI Chat shortly'}
          </p>
        )}
      </div>
      {actions}
    </div>
  </div>
);

export const MyNotesTab = ({ headers }) => {
  const [notes, setNotes] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/api/notes?mine=true`, { headers: headers() });
    if (res.ok) setNotes((await res.json()).notes || []);
    setLoaded(true);
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const withdraw = async (note) => {
    if (!window.confirm('Withdraw this pending note?')) return;
    const res = await fetch(`${API}/api/notes/${note.id}`, { method: 'DELETE', headers: headers() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(data.detail || 'Could not withdraw'); return; }
    toast.success('Note withdrawn');
    load();
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-sm font-bold text-gray-800 mb-1">My Guideline Notes</h2>
        <p className="text-[11px] text-gray-400 mb-4">One-off guidelines you've submitted. Approved notes become searchable in AI Chat for the whole team.</p>
        {loaded && notes.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
            <StickyNote size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-gray-400 text-sm">You haven't submitted any guideline notes yet</p>
            <p className="text-gray-300 text-xs mt-1">Use the "Add Note" button in the header to share underwriting intel</p>
          </div>
        )}
        <div className="space-y-2" data-testid="mynotes-list">
          {notes.map(n => (
            <NoteCard key={n.id} note={n}
              actions={n.status === 'pending' ? (
                <button onClick={() => withdraw(n)} className="text-gray-200 hover:text-red-400 p-1.5 rounded hover:bg-red-50 flex-shrink-0" title="Withdraw" data-testid={`withdraw-note-${n.id}`}>
                  <Trash2 size={12} />
                </button>
              ) : null}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
